import React, { createContext, useState, useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { App as AntdApp } from "antd";
import { useSelector } from "react-redux";

// Global styles
import "./App.scss";

// Pages
import Login from "./pages/login";
import PageNotFound from "./pages/pageNotFound/index";
import Password from "./pages/password";
import Dashboard from "./pages/dashboard";
import ForgotPassword from "./pages/forgotPassword";
import ResetPassword from "./pages/resetPassword";
import Shipment from "./pages/shipment";
import Portal from "./pages/portal";
import Transactions from "./pages/transactions";
import Checkout from "./pages/checkout";
import BackDashboard from "./pages/bkdashboard";

// Context
const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
});

function App() {
  const [theme, setTheme] = useState("light");
  const { currentUser } = useSelector((state) => state.user);
  const isDriver = currentUser?.roles?.includes("Driver");

  const allowedPermissions = currentUser?.["roles.permission"]?.map(
    (permission) => permission.name
  );
  const hasPermission = (permission) =>
    allowedPermissions?.includes(permission);

  const isAuthenticated = !!localStorage.getItem("token");

  const RedirectToLogin = () => {
    const location = useLocation();
    return (
      <Navigate
        to="/login"
        state={{ prevUrl: location.pathname + location.search }}
        replace
      />
    );
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // ✅ Detect route and load portal CSS conditionally
  const location = useLocation();
  useEffect(() => {
    let link = document.getElementById("portal-style");

    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "/portal.css";
      link.id = "portal-style";
      link.disabled = true;
      document.head.appendChild(link);
    }

    link.disabled = !(
      location.pathname.startsWith("/portal") ||
      location.pathname.startsWith("/transactions") ||
      location.pathname.startsWith("/checkout") ||
      location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/bkdashboard") ||
      location.pathname.startsWith("/shipment")
    );
  }, [location.pathname]);

  return (
    <div className="App">
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <AntdApp>
          <Routes>
            {/* Public routes */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/password-reset/:token" element={<ResetPassword />} />
            <Route path="/login" exact element={<Login />} />

            {/* Default dashboard (accessible if logged in, any role) */}
            <Route path="/" exact element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />

            {/* ✅ Driver-only routes */}
            <Route
              path="/portal"
              element={isAuthenticated ? <Portal /> : <RedirectToLogin />}
            />
            <Route
              path="/bkdashboard"
              element={isAuthenticated ? <BackDashboard /> : <RedirectToLogin />}
            />

            {/* ✅ Admin/Store-only routes */}
            <Route
              path="/shipment"
              element={
                isAuthenticated && !isDriver ? <Shipment /> : <RedirectToLogin />
              }
            />
            <Route
              path="/transactions"
              element={
                isAuthenticated && !isDriver ? <Transactions /> : <RedirectToLogin />
              }
            />
            <Route
              path="/checkout"
              element={
                isAuthenticated && !isDriver ? <Checkout /> : <RedirectToLogin />
              }
            />
            <Route
              path="/password"
              exact
              element={
                isAuthenticated && !isDriver ? <Password /> : <RedirectToLogin />
              }
            />

            {/* Fallback */}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </AntdApp>
      </ThemeContext.Provider>
    </div>
  );
}

export default App;
